import { useContext, useMemo, useState } from 'react';

import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { Button, Input, Tooltip } from 'antd';

import CircleButton from '@src/components/Buttons/CircleButton';
import ComponentBlocker from '@src/components/ComponentBlocker';
import { useSlidePanel } from '@src/components/SlidePanel';

import { ALLOWED_ROLES } from '@src/packages/utils';
import { SlidePanelContext } from '@src/packages/dashboard/components/SlidePanel/SlidePanelContext';

import styles from './styles.module.less';

/** Dashboard row from Firestore (organizations/{org}/dashboards or divisions/{div}/dashboards). */
export type MetricsBoardRow = {
  id: string;
  name?: string;
  divId?: string | null;
};

export type BoardSelection = {
  id: string | null;
  name?: string | null;
  divId?: string | null;
};

type DivisionsState = {
  divisions: {
    assigned: {
      data: {
        divisions: Record<string, { name?: string }>;
      };
    };
  };
};

type BoardListProps = {
  data: MetricsBoardRow[];
  boardSelected: BoardSelection;
  onBoardChange: (board: MetricsBoardRow) => void;
};

const buildBoardLabel = (board: MetricsBoardRow, divisions: DivisionsState['divisions']['assigned']['data']['divisions']) => {
  const name = board.name ?? '';
  const divName = board.divId ? divisions[board.divId]?.name : undefined;
  if (board.divId && divName) return `${name} · ${divName}`;
  return name;
};

const BoardList = ({ data, boardSelected, onBoardChange }: BoardListProps) => {
  const userOrgDivisions = useSelector(
    (state: DivisionsState) => state.divisions.assigned.data.divisions,
  );

  if (!data?.length) return null;

  return (
    <ul className={styles.boardList}>
      {data.map(board => {
        const tooltipTitle = buildBoardLabel(board, userOrgDivisions);

        return (
          <li key={board.id}>
            <Tooltip title={tooltipTitle} placement="topLeft" mouseEnterDelay={0.3}>
              <Button
                onClick={() => onBoardChange(board)}
                className={classNames(styles.boardItem, {
                  [styles.boardItemSelected]: boardSelected.id === board.id,
                })}
                block
                type="text"
              >
                <div className={styles.boardItemContent}>
                  <span className={styles.boardItemLine}>
                    <span className={styles.boardItemName}>{board.name ?? ''}</span>
                    {board.divId && userOrgDivisions[board.divId]?.name ? (
                      <>
                        <span className={styles.boardItemSep}>{' · '}</span>
                        <span className={styles.boardItemDiv}>{userOrgDivisions[board.divId]?.name}</span>
                      </>
                    ) : null}
                  </span>
                </div>
              </Button>
            </Tooltip>
          </li>
        );
      })}
    </ul>
  );
};

const DashboardMenu = () => {
  const intl = useIntl();
  const [search, setSearch] = useState('');

  const { isOpen } = useSlidePanel();

  const { onOpenBoardForm, boardsByOrg, boardsByDiv, onBoardChange, boardSelected } =
    useContext(SlidePanelContext);

  const filteredByOrg = useMemo(() => {
    if (!search) return boardsByOrg;
    return boardsByOrg.filter(b => b.name?.toLowerCase().includes(search.toLowerCase()));
  }, [boardsByOrg, search]);

  const filteredByDiv = useMemo(() => {
    if (!search) return boardsByDiv;
    return boardsByDiv.filter(b => b.name?.toLowerCase().includes(search.toLowerCase()));
  }, [boardsByDiv, search]);

  if (!isOpen) {
    return (
      <div className={styles.collapsedContent}>
        <CircleButton type="soft" iconName="search" iconSize="sm" />

        <div className={styles.collapsedSection}>
          <span className={styles.collapsedLabel}>
            {intl.formatMessage({ id: 'dashboards.metrics.board.organization' }).slice(0, 3)}
          </span>
          <ComponentBlocker allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.DASHBOARDS.CREATE}>
            <CircleButton
              onClick={() => onOpenBoardForm('organization')}
              type="primary"
              iconName="add"
              iconSize="sm"
            />
          </ComponentBlocker>
        </div>

        <div className={styles.collapsedSection}>
          <span className={styles.collapsedLabel}>
            {intl.formatMessage({ id: 'dashboards.metrics.board.division' }).slice(0, 3)}
          </span>
          <ComponentBlocker allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.DIVISIONS.DASHBOARDS.CREATE}>
            <CircleButton
              onClick={() => onOpenBoardForm('division')}
              type="primary"
              iconName="add"
              iconSize="sm"
            />
          </ComponentBlocker>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.expandedContent}>
      <div className={styles.searchWrapper}>
        <Input
          prefix={<span className="material-symbols-outlined">search</span>}
          placeholder={intl.formatMessage({ id: 'dashboards.menu.search.placeholder' })}
          value={search}
          onChange={e => setSearch(e.target.value)}
          allowClear
          size="small"
        />
      </div>

      <div className={styles.sections}>
        <ComponentBlocker allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.DASHBOARDS.LIST}>
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionTitle}>
                {intl.formatMessage({ id: 'dashboards.metrics.board.organization' })}
              </span>
              <ComponentBlocker allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.DASHBOARDS.CREATE}>
                <CircleButton
                  onClick={() => onOpenBoardForm('organization')}
                  type="primary"
                  iconName="add"
                  iconSize="sm"
                />
              </ComponentBlocker>
            </div>
            <BoardList
              data={filteredByOrg}
              boardSelected={boardSelected}
              onBoardChange={onBoardChange}
            />
          </div>
        </ComponentBlocker>

        <hr className={styles.separator} />

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionTitle}>
              {intl.formatMessage({ id: 'dashboards.metrics.board.division' })}
            </span>
            <ComponentBlocker
              allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.DIVISIONS.DASHBOARDS.CREATE}
            >
              <CircleButton
                onClick={() => onOpenBoardForm('division')}
                type="primary"
                iconName="add"
                iconSize="sm"
              />
            </ComponentBlocker>
          </div>
          <BoardList
            data={filteredByDiv}
            boardSelected={boardSelected}
            onBoardChange={onBoardChange}
          />
        </div>
      </div>
    </div>
  );
};

export default DashboardMenu;
